import { useEffect, useState } from "react";
import { errorToastMessage } from "../../../utils/toast";
import http from "../../../utils/http";
import { useParams } from "react-router-dom";
import FormPrint from "./FormPrint";
import { AxiosResponse } from "axios";
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { sortQuestionsWithGroups } from "../../../utils/groupQuestionOrder";
import i18n from "../../../i18n";
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from "../../../i18n/constants";
import { contentDisplayName } from "../../../utils/contentDisplayName";

export interface PrintForm {
  id?: string;
  name: string;
  formName: string;
  phase: string;
  phaseType?: string;
  questions: any[];
}

const messageKeyMap: Record<string, string> = {
  visit: "SURVEY.PRINT_TYPE_VISIT_FORMS",
  repeated_data: "SURVEY.PRINT_TYPE_REPEATED_DATA_FORMS",
  survey: "SURVEY.PRINT_TYPE_SURVEY_FORMS",
  screening: "SURVEY.PRINT_TYPE_SCREENING_FORMS",
};

export const typeLabelKeyMap: Record<string, string> = {
  visit: "SURVEY.PRINT_TYPE_VISIT",
  repeated_data: "SURVEY.PRINT_TYPE_REPEATED_DATA",
  survey: "SURVEY.PRINT_TYPE_SURVEY",
  screening: "SURVEY.PRINT_TYPE_SCREENING",
};

const QuestionPrint = () => {
  const { id, type } = useParams();
  const [loading, setLoading] = useState(true);
  const [questionError, setQuestionError] = useState(false);
  const [forms, setForms] = useState<PrintForm[]>();
  const [languages, setLanguages] = useState<
    { languageCode: string; languageLabel: string }[]
  >([]);
  const [langCode, setLangCode] = useState(
    (SUPPORTED_LANGUAGES as readonly string[]).includes(i18n.language)
      ? i18n.language
      : DEFAULT_LANGUAGE
  );
  const tPrint = i18n.getFixedT(
    (SUPPORTED_LANGUAGES as readonly string[]).includes(langCode)
      ? langCode
      : DEFAULT_LANGUAGE,
    "responses"
  );
  const printLanguageLabel = tPrint("SURVEY.PRINT_LANGUAGE_LABEL") as string;

  // This page opens in its own tab (window.open), outside StudyLayout, so
  // state.study.languages isn't populated here — fetch the study's languages directly.
  useEffect(() => {
    if (!id) {
      return;
    }
    const fetchLanguages = async () => {
      try {
        const { data } = await http.get(`/study/${id}/settings/languages`);
        setLanguages(data?.data || []);
      } catch {
        // Non-critical: selector just falls back to English-only.
      }
    };
    fetchLanguages();
  }, [id]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res: AxiosResponse = await http.get(
          `/study/${id}/study-phase`,
          {
            params: {
              category: type,
              lang: langCode && langCode !== "en" ? langCode : undefined,
            },
          }
        );
        const phaseData = res.data?.data;
        const formattedData = phaseData
          ?.map((item: any) => {
            const forms = item?.phaseForms.map((pf: any) => {
              return {
                position: pf.position,
                formId: pf.form.id,
              };
            });
            forms.sort((a: any, b: any) => a.position - b.position);
            return {
              id: item?.id,
              name: contentDisplayName(item) || "",
              position: item?.position || 1,
              forms: forms,
            };
          })
          .sort((a: any, b: any) => a.position - b.position);
        const result = [];
        let position = 1;
        for (let i = 0; i < formattedData.length; i++) {
          const phase = formattedData[i];
          for (let j = 0; j < phase.forms.length; j++) {
            const form = phase.forms[j];
            const { data } = await http.get(
              `/study/${id}/forms/${form.formId}`,
              {
                params:
                  langCode && langCode !== "en" ? { lang: langCode } : undefined,
              }
            );
            let questions = sortQuestionsWithGroups(data.data.questions ?? []);
            questions = questions.map((q: any) => {
              if (q.choices) {
                q.choices = q.choices.sort(
                  (a: { position: number }, b: { position: number }) =>
                    a.position - b.position
                );
              }
              q.labelMarkdown = q.label.replace(/(?:\r\n|\r|\n)/g, "<br>");
              return {
                ...q,
              };
            });
            result.push({
              id: form.formId,
              name: position++ + ". " + phase.name + " : " + data.data.name,
              questions,
              phase: phase.name,
              formName: data.data.name,
            });
          }
        }
        setForms(result);
        setLoading(false);
      } catch (err) {
        errorToastMessage(err as Error);
        setLoading(false);
        setQuestionError(true);
      }
    };
    fetchData();
  }, [id, type, langCode]);

  const startPrint = () => {
    window.print();
  };

  return loading ? (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
      }}
    >
      {tPrint("SURVEY.PRINT_LOADING") as string}
    </div>
  ) : questionError ? (
    <div>{tPrint("SURVEY.PRINT_ERROR") as string}</div>
  ) : (
    <div className="study-print-container">
      <div
        className="print-button"
        style={{ alignItems: "center", gap: "16px" }}
      >
        <FormControl sx={{ width: "160px" }}>
          <InputLabel id="print-phase-language-label">
            {printLanguageLabel}
          </InputLabel>
          <Select
            value={langCode}
            onChange={(e) => setLangCode(e.target.value)}
            id="printPhaseLang"
            label={printLanguageLabel}
            labelId="print-phase-language-label"
            sx={{ height: "36px" }}
          >
            {(languages.length
              ? languages
              : [{ languageCode: "en", languageLabel: "English" }]
            ).map((lang) => (
              <MenuItem key={lang.languageCode} value={lang.languageCode}>
                {lang.languageLabel}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="contained" onClick={startPrint}>
          {tPrint("SURVEY.PRINT") as string}
        </Button>
      </div>
      <h1 style={{ textAlign: "center", marginBottom: "16px" }}>
        {tPrint(messageKeyMap[type || ""] || "") as string}
      </h1>
      <>
        {forms?.map((form, i) => (
          <FormPrint
            key={form.id}
            form={form}
            breakPage={i === 0 ? false : true}
            langCode={langCode}
          />
        ))}
      </>
      <div
        className="print-form-container"
        style={{ pageBreakBefore: "always" }}
      >
        <h1>
          {tPrint("SURVEY.PRINT_LIST_OF_FORMS_TITLE", {
            type: tPrint(typeLabelKeyMap[type || ""] || ""),
          }) as string}
        </h1>
        <div style={{ marginTop: "20px" }}>
          <table className="print-list-table">
            <thead>
              <tr>
                <td>
                  {tPrint("SURVEY.PRINT_TYPE_NAME_HEADER", {
                    type: tPrint(typeLabelKeyMap[type || ""] || ""),
                  }) as string}
                </td>
                <td>{tPrint("SURVEY.PRINT_FORM_NAME_HEADER") as string}</td>
              </tr>
            </thead>
            <tbody>
              {forms?.map((form) => (
                <tr key={form.id}>
                  <td>{form.phase}</td>
                  <td>{form.formName}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default QuestionPrint;
